import * as React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Popper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import api from "../../pages/api/base-url";
import createVersion from "../../pages/api/versions/create-version";
import updateAppRequest from "../../pages/api/apps/patch-app";
import fileSelect from "../../utils/get-info-from-file";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { Application, Version } from "./apps-types";
import { Group } from "../users/users-types";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import checkPlatform from "../../utils/checkPlatform";
import UploadAppSummary from "./UploadingAppSummary";
import { useSWRConfig } from "swr";
import { useApps, useGroups } from "../../pages/api/api";
import deleteApp from "../../pages/api/apps/delete-app";

const CustomPaper = (props: any) => {
  return <Paper elevation={8} {...props} />;
};

const CustomPopper = (props: any) => {
  const modifiers = [
    {
      name: "flip",
      options: {
        fallbackPlacements: [],
      },
    },
  ];

  return (
    <Popper
      {...props}
      modifiers={modifiers}
      popperOptions={{
        placement: "bottom",
      }}
    />
  );
};

export default function UploadingAppForm(props: {
  buttonText: string;
  dialogTitle: string;
  expectedAppID?: number;
  isDashboard: boolean;
  openDialog?: boolean;
  updateOpenUploadDialog?: any;
  file?: File;
  updateFile?: any;
  versions?: Version[];
}) {
  const { mutate } = useSWRConfig();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const { t } = useTranslation();

  /* API Calls to populate Autocomplete components options to be chosen from the list */
  const { apps, isLoadingApps, isErrorApps } = useApps();
  const { groups, isLoadingGroups, isErrorGroups } = useGroups();

  // 1. Section 1: File browse
  const [file, setFile] = useState<File | null>(
    props.file !== undefined ? props.file : null
  );
  const [fileName, setFileName] = useState("");
  const [isNewApp, setNewApp] = useState<boolean>(true);
  const [alreadyPresentVersion, setAlreadyPresentVersion] =
    useState<boolean>(false);
  //const [fileNotPresent, setFileNotPresent] = useState(false);

  // 2. Section 2: Application fields of interest
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [bundleID, setBundleID] = useState("");
  const [icon, setIcon] = useState("");
  const [platform, setPlatform] = useState("");

  const [errorName, setErrorName] = useState(false);

  /* 3.a Section 3: To upload an Android version to an iOS app 
  (or viceversa -> from monoplatform to cross-platform) */
  const [wrongAppId, setWrongAppId] = useState<boolean>(false);
  const [appId, setAppId] = useState<number>();
  const [confirmNewApp, setConfirmNewApp] = useState(true);
  const [selectedApp, setSelectedApp] = React.useState<Application | null>(
    null
  );
  const [appNameAlreadyPresent, setAppNameAlreadyPresent] =
    useState<boolean>(false);

  /* 3.b Section 3: Select group to add your application to. 
  This step is optional in case the user is loading a new version 
  of an application already assigned to a group. */
  const [group, setGroup] = useState<Group | null>();
  const [groupID, setGroupID] = useState<number>();

  /* 4. Section 4: App description or version changelog. This step is optional in case 
  the user is loading a new version of an already present application, 
  while it is mandatory for older applications. */
  const [description, setDescription] = useState("");
  const [appDescription, setAppDescription] = useState("");
  const [errorDescription, setErrorDescription] = useState(false);

  const [disableAppSelection, setDisableAppSelection] = useState(false);
  const [disableGroupSelection, setDisableGroupSelection] = useState(false);

  /* 5. Section 5: variables to populate App upload summary dialog */
  const [loadingAppInfo, setLoadingAppInfo] = useState(true);
  const [openUploadedAppSummary, setOpenUploadedAppSummary] = useState(false);
  const [uploadedVersion, setUploadedVersion] = useState<Version>();

  const [uploadedApplicationName, setUploadedApplicationName] =
    useState<string>();
  const [appLoading, setAppLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<number>(0);
  //const [uploadedAppInfoLoading, setUploadedAppInfoLoading] = useState(false);

  /*TO BE TESTED */
  const [checked, setChecked] = React.useState(false);

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);

    /* TODO */
    if (event.target.checked) {
      setGroup(null);
    } else {
      setSelectedApp(null);
    }
  };

  function resetAll() {
    setAppDescription("");
    setFileName("");
    setName("");
    setVersion("");
    setBundleID("");
    setIcon("");
    setDescription("");
    setPlatform("");
    setErrorName(false);
    setDisableGroupSelection(false);
    setDisableAppSelection(false);
    setLoadingAppInfo(true);
    setOpenUploadedAppSummary(false);
    setSelectedApp(null);
    setGroup(null);
    setGroupID(undefined);
  }

  const handleCloseDialog = () => {
    resetAll();
    props.updateOpenUploadDialog(false);
    setNewApp(true);
    setAlreadyPresentVersion(false);
    setAppNameAlreadyPresent(false);
    setWrongAppId(false);
    setConfirmNewApp(true);
    //setSelectedApp(null);
    setFileName("");
    props.file !== undefined && props.file !== null && props.updateFile(null);
    setFile(null);
  };

  const handleOpenAppSummaryDialog = () => {
    handleCloseDialog();
    setOpenUploadedAppSummary(true);
    setAppLoading(true);
  };

  const Input = styled("input")({
    display: "none",
  });

  const handleSelectApp = (event: any, newValue: Application | null) => {
    setSelectedApp(newValue);
    if (newValue !== null && newValue !== undefined) {
      setConfirmNewApp(false);
      setAppDescription(newValue.description);
      //setDisableGroupSelection(true);
      setDisableAppSelection(false);
    } else {
      setConfirmNewApp(true);
      //setDisableGroupSelection(false);
    }
  };

  const handleSelectGroup = (
    event: React.SyntheticEvent,
    newValue: Group | null
  ) => {
    setGroup(newValue);
    setGroupID(newValue?.id);

    if (newValue) {
      setDisableGroupSelection(false);
      //setDisableAppSelection(true);
    } else {
      //setDisableAppSelection(false);
    }

    // setGroupID( [...groupID, newValue.id])
    //setMissingGroup(false);
  };

  const isConfirmButtonEnabled =
    file &&
    file !== null &&
    //!alreadyPresentVersion &&
    name !== "" &&
    ((isNewApp &&
      confirmNewApp &&
      group &&
      group !== null &&
      description !== "") ||
      (isNewApp && !confirmNewApp && selectedApp && selectedApp !== null) ||
      (!isNewApp && !confirmNewApp));

  /* --------------------------------------------------------------------------- */

  /* Function called when the user is loading a completely new application, never seen before.
  A POST request to /apps endpoint and one to /versions are performed. */
  async function createApp(file: File) {
    const now = new Date();
    const date = now.toISOString();

    try {
      let payload = {
        name: name,
        description: description,
        icon: icon,
        groupId: groupID ? groupID : group?.id,
      };
      let res = await api.post("/apps", payload);
      setAppLoading(true);
      setRequestStatus(res.status);

      if (res.status >= 400 && res.status < 500) {
        setAppLoading(false);
      }
      if (res.status >= 200 && res.status < 300) {
        const postResult = createVersion(
          String(res.data.id),
          version,
          description,
          date,
          platform,
          bundleID,
          file
        );
        const result = await postResult;
        if (result.status >= 400 && result.status < 500) {
          setAppLoading(false);
        }

        setRequestStatus(result.status);
        if (result.status >= 200 && result.status < 300) {
          const versionObject: Version = {
            id: result.data.id,
            versionNumber: version,
            changelog: description,
            bundleId: bundleID,
            updated_at: now.toISOString(),
            created_at: now.toISOString(),
            platform: platform,
          };
          setUploadedVersion(versionObject);
          setUploadedApplicationName(name);
          mutate(`/apps`);
          mutate(`/links`);
          setAppLoading(false);
        }
      }
    } catch (err: any) {
      if (err.response) {
        setAppLoading(false);
        setRequestStatus(err.response.status);
        //Not in 200 response range
        console.log(err.response.data);
        console.log(err.response.status);
      } else {
        console.log(`Error: ${err.message}`);
      }
    }
  }

  /* When a file is uploaded, its BundleID and version are checked and APPID is set */
  const checkBundle = React.useCallback(
    async (bundleID: string, version: string) => {
      try {
        let res = await api.get(`/versions/getapp?bundleId=${bundleID}`);

        if (res.status >= 200 && res.status < 300) {
          /* 1. BUNDLE ID Already present in archive => !isNewApp and !confirmNewApp */
          setNewApp(false);
          setConfirmNewApp(false);
          setLoadingAppInfo(false);

          if (!props.expectedAppID) {
            /* Caricamento che avviene dalla dashboard */
            setAppId(res.data.id);
            setAppDescription(res.data.description);
          } else {
            /* Caricamento che avviene dalla pagina dell'app e il bundleID corrisponde a quello dell'app */
            if (res.data.id === props.expectedAppID) {
              setAppId(res.data.id);
              setWrongAppId(false);
              setAppDescription(res.data.description);
            } else {
              /* Caricamento che avviene dalla pagina dell'app e il bundleID NON corrisponde a quello dell'app */
              setWrongAppId(true);
            }
          }
          if (
            res.data.versions.some(
              (item: Version) =>
                item.versionNumber === version && item.platform === platform
            )
          ) {
            setAlreadyPresentVersion(true);
          } else {
            setAlreadyPresentVersion(false);
          }
        } else if (res.status >= 400 && res.status < 500) {
          /* 2. BUNDLE ID NON PRESENTE = isNewApp --> true */
          setNewApp(true);
          setConfirmNewApp(true);
          setLoadingAppInfo(false);
          if (isNewApp && !props.isDashboard) setWrongAppId(true);
        }
      } catch (err: any) {
        if (err.response) {
          setNewApp(true);
          setConfirmNewApp(true);
          setAlreadyPresentVersion(false);
          setLoadingAppInfo(false);
          if (isNewApp && !props.isDashboard) setWrongAppId(true);

          //Not in 200 response range
          console.log(err.response.data);
          console.log(err.response.status);
        } else {
          console.log(`Error: ${err.message}`);
        }
      }
    },
    [props.expectedAppID, props.isDashboard, isNewApp]
  );

  /* This function parses the uploaded file */
  useEffect(() => {
    if (props.file !== undefined && props.file !== null) {
      setFile(props.file);
    }

    (async () => {
      if (file === null) {
        setFileName("");
        setName("");
        setVersion("");
        setBundleID("");
        setIcon("");
        setPlatform("");
        return;
      }

      if (file) {
        const fileInfo = await fileSelect(file);
        checkBundle(fileInfo.packageName, fileInfo.version);

        // Shows the information into the correct fields
        setFileName(file?.name);
        setName(fileInfo.appName);
        setVersion(fileInfo.version);
        setBundleID(fileInfo.packageName);
        setIcon(fileInfo.icon);
        setPlatform(fileInfo.platform);
      }
    })();
  }, [props.file, file, checkBundle]);

  useEffect(() => {
    const isAppNameAlreadyPresent =
      apps.filter((app) => app.name === name).length > 0;

    const temp = apps.filter((app) => app.name === name)[0];

    if (isNewApp && isAppNameAlreadyPresent) {
      console.log("NAME", name);
      console.log("app name trovato");
      setAppNameAlreadyPresent(true);
      console.log("app name set");
      console.log(appNameAlreadyPresent);
      setGroupID(temp.groupId);
      setGroup(groups[temp.groupId]);
      setDescription(temp.description);
    } else {
      console.log("app name non trovato");
      setAppNameAlreadyPresent(false);
    }

    console.log("Already present 2?", appNameAlreadyPresent);
  }, [apps, name, appNameAlreadyPresent, groups, isNewApp]);

  function handleChangeName(event: React.ChangeEvent<HTMLInputElement>) {
    setName(event.target.value as string);
    const input = event.target.value as string;

    if (input !== "") {
      setErrorName(false);
      setName(input);
      if (apps.filter((app) => app.name !== name)) {
        setAppNameAlreadyPresent(false);
      } else {
        setAppNameAlreadyPresent(true);
      }
    } else {
      setErrorName(true);
    }
  }

  function handleChangeDescription(event: React.ChangeEvent<HTMLInputElement>) {
    setDescription(event.target.value as string);
    const input = event.target.value as string;

    if (input !== "") {
      setErrorDescription(false);
      setDescription(input);
    }
  }

  async function handleSubmit() {
    const now = new Date();
    const date = now.toISOString();

    if (file === null || file === undefined) {
      return;
    }

    if (description === "") {
      if (confirmNewApp) {
        setErrorDescription(true);
        return;
      } else {
        setErrorDescription(false);
      }
    } else {
      setErrorDescription(false);
    }

    /* Case 1: A new BundleID is found and the user confirms it is a completely new application
     (isNewApp === true and confirNewApp === true) */
    if (isNewApp && confirmNewApp && file) {
      if (appNameAlreadyPresent) {
        const temp = apps.find((app) => app.name === name);
        if (temp) {
          await deleteApp(temp.id);
        } else {
          console.log("");
        }
      }
      createApp(file);
    } else if (isNewApp && !confirmNewApp && file) {
      (async () => {
        setAppLoading(true);
        try {
          /* Case 2: A new BundleID is found but the user selects one application from the list  */

          const postResult = createVersion(
            String(selectedApp?.id),
            version,
            description,
            date,
            platform,
            bundleID,
            file
          );
          const result = await postResult;
          setRequestStatus(result.status);

          if (result.status >= 400 && result.status < 500) {
            setAppLoading(false);
          }

          if (result.status >= 200 && result.status < 300 && selectedApp?.id) {
            updateAppRequest(selectedApp.id, name, appDescription, icon);

            if (props.isDashboard) {
              mutate(`/apps`);
              mutate(`/links`);
            }

            const versionObject: Version = {
              id: result.data.id,
              versionNumber: version,
              changelog: description,
              bundleId: bundleID,
              updated_at: now.toISOString(),
              created_at: now.toISOString(),
              platform: platform,
            };
            setUploadedVersion(versionObject);
            setUploadedApplicationName(name);
            setAppLoading(false);
          }
        } catch (err: any) {
          if (err.response) {
            setAppLoading(false);
            setRequestStatus(err.response.status);
            //Not in 200 response range
            console.log(err.response.data);
            console.log(err.response.status);
          } else {
            console.log(`Error: ${err.message}`);
          }
        }
      })();

      /* Case 3: Old BundleID already found in archive (isNewApp === false and confirNewApp === false) */
    } else if (!isNewApp && !confirmNewApp && file) {
      (async () => {
        setAppLoading(true);
        try {
          /* Case 2: app already present */

          if (alreadyPresentVersion) {
            const tempVersion = apps
              .find((app) => app.name === name)
              ?.versions.find(
                (versionInfo) => versionInfo.versionNumber === version
              );
            if (tempVersion) {
              await api.delete(`/versions/${tempVersion.id}`);
            } else {
              console.log("");
            }
          }

          const postResult = createVersion(
            String(appId),
            version,
            description,
            date,
            platform,
            bundleID,
            file
          );

          const result = await postResult;
          setRequestStatus(result.status);
          if (result.status >= 400 && result.status < 500) {
            setAppLoading(false);
          }
          if (result.status >= 200 && result.status < 300 && appId) {
            updateAppRequest(appId, name, appDescription, icon);

            if (props.isDashboard) {
              mutate(`/apps`);
              mutate(`/links`);
            } else {
              mutate(`/apps/${appId}`);
              mutate("/apps");
            }

            const versionObject: Version = {
              id: result.data.id,
              versionNumber: version,
              changelog: description,
              bundleId: bundleID,
              updated_at: now.toISOString(),
              created_at: now.toISOString(),
              platform: platform,
            };
            setUploadedVersion(versionObject);
            setUploadedApplicationName(name);
            setAppLoading(false);
          }
        } catch (err: any) {
          if (err.response) {
            setAppLoading(false);
            setRequestStatus(err.response.status);
            //Not in 200 response range
            console.log(err.response.data);
            console.log(err.response.status);
          } else {
            console.log(`Error: ${err.message}`);
          }
        }
      })();
    }
    handleOpenAppSummaryDialog();
  }

  return (
    <Box>
      {props.openDialog && (
        <Dialog
          PaperProps={{ style: { overflowY: "visible" } }}
          fullScreen={fullScreen}
          open={props.openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {props.dialogTitle}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mb: 1 }}>
              <Divider sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("Choose the .ipa or .apk file you want to upload.")}
                </Typography>
              </Divider>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                padding={2}
                spacing={1}
              >
                <label htmlFor="contained-button-file">
                  <Input
                    id="contained-button-file"
                    type="file"
                    name="file"
                    accept=".ipa, .apk"
                    onChange={(e) => {
                      props.file !== undefined &&
                        props.file !== null &&
                        props.updateFile(null);
                      e.currentTarget.files &&
                        setFile(e.currentTarget.files?.[0]);
                    }}
                  />
                  <Button variant="contained" component="span">
                    {t("Browse")}
                  </Button>
                </label>
                <TextField
                  fullWidth
                  id="standard"
                  placeholder={t("File Name")}
                  variant="standard"
                  value={fileName}
                />
              </Stack>
            </Stack>
            {file && loadingAppInfo && (
              <Box
                sx={{ display: "flex" }}
                alignItems="center"
                justifyContent="center"
                height="20vh"
              >
                <CircularProgress />
              </Box>
            )}
            {file && (
              /*!alreadyPresentVersion &&
              !loadingAppInfo &&
              !wrongAppId &&*/ <Grid container sx={{ my: 2 }}>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("Information")}
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Avatar
                      alt="app-icon"
                      variant="square"
                      src={icon}
                      id="icon"
                      sx={{ width: 60, height: 60 }}
                    >
                      <HourglassEmptyIcon />
                    </Avatar>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      onChange={handleChangeName}
                      margin="dense"
                      id="name"
                      label={t("Application Name")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={name}
                      error={errorName}
                      required={name === ""}
                      helperText={
                        name === ""
                          ? t("Application name cannot be empty.")
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      margin="dense"
                      id="version"
                      label={t("Version")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={version}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      margin="dense"
                      id="platform"
                      label={t("Platform")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={platform === "IOS" ? "iOS" : "Android"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      margin="dense"
                      id="bundle"
                      label={t("Bundle Identifier")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={bundleID}
                    />
                  </Grid>
                </Grid>
                {isNewApp && appNameAlreadyPresent && (
                  <Box sx={{ width: "100%", mb: 2, mt: 2 }}>
                    <Alert severity="warning">
                      <AlertTitle>{t("Warning")}</AlertTitle>
                      {t(
                        "There is already another application with the same name. If you proceed, the oldest one will be removed, otherwise change application name using this form."
                      )}
                    </Alert>
                  </Box>
                )}
                {alreadyPresentVersion && (
                  <Box sx={{ width: "100%", mb: 2, mt: 2 }}>
                    <Alert severity="warning">
                      <AlertTitle>{t("Warning")}</AlertTitle>
                      {t(
                        "This version is already present. Do you want to replace the existing one?"
                      )}
                    </Alert>
                  </Box>
                )}
                {wrongAppId && (
                  <Box sx={{ width: "100%", mb: 2, mt: 2 }}>
                    <Alert severity="error">
                      <AlertTitle>{t("Error")}</AlertTitle>
                      {t(
                        "You are trying to upload a wrong application. Retry!"
                      )}
                    </Alert>
                  </Box>
                )}

                {!isNewApp && !alreadyPresentVersion && !wrongAppId && (
                  <Box sx={{ width: "100%", mb: 2, mt: 2 }}>
                    <Alert severity="success">
                      <AlertTitle>{t("New version")}</AlertTitle>
                      {t(
                        "You are loading a new version of an already present application."
                      )}
                    </Alert>
                  </Box>
                )}

                {isNewApp && !appNameAlreadyPresent && (
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    <Grid item xs={12}>
                      <Alert severity="success">
                        <AlertTitle>{t("New application")}</AlertTitle>
                        {t(
                          "If you are loading a new application, select a group to add your application to. Otherwise, choose an application if you are uploading a cross-platform version of an already existing application."
                        )}
                      </Alert>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1, mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {t("Settings")}
                        </Typography>
                      </Divider>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={checked}
                            inputProps={{ "aria-label": "controlled" }}
                            onChange={handleCheckChange}
                          />
                        }
                        label="Cross-Platform"
                        labelPlacement="end"
                      />
                    </Grid>
                    {checked && apps && (
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          value={selectedApp}
                          disablePortal={true}
                          id="combo-box-demo"
                          PaperComponent={CustomPaper}
                          PopperComponent={CustomPopper}
                          disabled={disableAppSelection}
                          options={
                            platform === "IOS"
                              ? apps
                                  ?.filter(
                                    (app) =>
                                      checkPlatform(app.versions) === "Android"
                                  )
                                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                              : apps
                                  ?.filter(
                                    (app) =>
                                      checkPlatform(app.versions) === "iOS"
                                  )
                                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                          }
                          onChange={handleSelectApp}
                          getOptionLabel={(option) => option.name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("Applications")}
                              helperText={t(
                                "Select an application if you are uploading a cross-platform version."
                              )}
                            />
                          )}
                          ListboxProps={{
                            style: {
                              maxHeight: 150,
                              border: "1px solid",
                            },
                          }}
                        />
                      </Grid>
                    )}
                    {!checked && groups && (
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          value={group}
                          id="combo-box-demo"
                          PaperComponent={CustomPaper}
                          PopperComponent={CustomPopper}
                          onChange={handleSelectGroup}
                          disabled={disableGroupSelection}
                          options={groups.sort((a, b) =>
                            a.name > b.name ? 1 : -1
                          )}
                          getOptionLabel={(option) => option.name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("Group")}
                              placeholder={t("Select Group")}
                              helperText={t(
                                "Select a group if you are uploading a new application."
                              )}
                            />
                          )}
                          ListboxProps={{
                            style: {
                              maxHeight: 150,
                              border: "1px solid",
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
                <Grid container spacing={1} alignItems="center" sx={{ my: 2 }}>
                  <Grid item xs={12}>
                    <Divider>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("Description")}
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      onChange={handleChangeDescription}
                      margin="dense"
                      id="description"
                      label={
                        isNewApp && confirmNewApp
                          ? t("Description")
                          : t("Description (optional)")
                      }
                      type="text"
                      fullWidth
                      multiline
                      rows={2}
                      inputProps={{ maxLength: 60 }}
                      variant="outlined"
                      value={description}
                      required={isNewApp && confirmNewApp}
                      error={errorDescription}
                      helperText={
                        isNewApp && confirmNewApp
                          ? t(
                              "You are loading a new application. A description is required."
                            )
                          : ""
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
            <Button onClick={handleSubmit} disabled={!isConfirmButtonEnabled}>
              {t("Upload")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {
        <UploadAppSummary
          loading={appLoading}
          name={uploadedApplicationName}
          status={requestStatus}
          uploadedVersion={uploadedVersion}
          open={openUploadedAppSummary}
          setOpen={setOpenUploadedAppSummary}
        />
      }
    </Box>
  );
}
